// リアルタイムバトル進行画面
// カードアニメーション、パワー比較、フラッグ奪取演出を強化
// マルチプレイ対応版 - 完全な盤面状態同期

import { render } from "./components";
import { showCardDetail } from "./CardDetailModal";
import { showSwapModal } from "./SwapCardModal";
import { showMoveCardModal } from "./MoveCardModal";
import { MatchPhase } from "../game/MatchPhase";
import { CardInstance, MatchState, CardDef } from "../types";
import { getCardById, ALL_CARDS } from "../cards";
import { 
  getMultiplayerManager, 
  MatchSyncState, 
  CardInstanceSync,
  PlayerFieldSync,
  BenchSlotSync
} from "../sdk";

const BOT_TURN_DELAY = 1200;
const CARD_REVEAL_DELAY = 800;
const FLAG_TRANSFER_DELAY = 2000;
const MULTIPLAYER_POLL_INTERVAL = 500;

interface BattleUIState {
  matchPhase: MatchPhase | null;
  myPlayerId: string;
  opponentPlayerId: string;
  isMyTurn: boolean;
  opponentIsBot: boolean;
  botTimer: number | null;
  lastDrawnCard: CardInstance | null;
  showFlagCapture: boolean;
  flagCapturer: "me" | "opponent" | null;
  isAnimating: boolean;
  lastAbilityMessage: string | null;
  // マルチプレイ用
  isMultiplayer: boolean;
  isHost: boolean;
  matchIndex: number;  // マッチインデックス (複数マッチ対応)
  lastSyncTime: number;
  pollTimer: number | null;
  waitingForOpponent: boolean;
  // 効果待機中のフラグ
  pendingFlagCapture: boolean;
  // 同期完了コールバック
  onSyncComplete: (() => void) | null;
}

let battleState: BattleUIState = {
  matchPhase: null,
  myPlayerId: "",
  opponentPlayerId: "",
  isMyTurn: false,
  opponentIsBot: false,
  botTimer: null,
  lastDrawnCard: null,
  showFlagCapture: false,
  flagCapturer: null,
  isAnimating: false,
  lastAbilityMessage: null,
  isMultiplayer: false,
  isHost: false,
  matchIndex: 0,
  lastSyncTime: 0,
  pollTimer: null,
  waitingForOpponent: false,
  pendingFlagCapture: false,
  onSyncComplete: null,
};

const cardDefs = new Map(ALL_CARDS.map((c) => [c.id, c]));

function injectBattleStyles(): void {
  if (document.getElementById("match-battle-styles")) return;
  const style = document.createElement("style");
  style.id = "match-battle-styles";
  style.textContent = `
    .match-battle-container { min-height: 100vh; background: linear-gradient(180deg, #0a0a1a 0%, #1a0a2e 50%, #0a1a2e 100%); display: flex; flex-direction: column; }
    .battle-header { text-align: center; padding: 10px; background: rgba(0,0,0,0.4); }
    .battle-title { color: #ff6b6b; font-size: 1rem; font-weight: bold; }
    .turn-indicator { color: #ffd700; font-size: 0.8rem; margin-top: 2px; }
    .battle-main { flex: 1; display: flex; gap: 8px; padding: 8px; overflow: hidden; }
    .battle-field { flex: 2; display: flex; flex-direction: column; gap: 8px; }
    .player-zone { flex: 1; background: rgba(0,0,0,0.3); border-radius: 10px; padding: 10px; border: 2px solid #333; position: relative; }
    .player-zone.has-flag { box-shadow: 0 0 25px rgba(255, 215, 0, 0.4); border-color: rgba(255, 215, 0, 0.6); }
    .player-zone.is-attacker { background: rgba(255, 107, 107, 0.1); border-color: rgba(255, 107, 107, 0.4); }
    .zone-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
    .player-name { font-weight: bold; font-size: 0.85rem; }
    .player-name.me { color: #00fff5; }
    .player-name.opponent { color: #ff6b6b; }
    .attacker-badge { background: linear-gradient(135deg, #ff6b6b, #ff8e53); color: #fff; padding: 2px 8px; border-radius: 4px; font-size: 0.65rem; font-weight: bold; animation: pulse 1s infinite; }
    .defender-badge { background: linear-gradient(135deg, #3498db, #2980b9); color: #fff; padding: 2px 8px; border-radius: 4px; font-size: 0.65rem; font-weight: bold; }
    @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }
    .zone-content { display: flex; gap: 10px; align-items: flex-start; }
    .flag-area { flex: 0 0 80px; text-align: center; }
    .flag-label { color: #ffd700; font-size: 0.7rem; margin-bottom: 4px; }
    .flag-card { position: relative; animation: flagWave 2s ease-in-out infinite; }
    @keyframes flagWave { 0%, 100% { transform: rotate(-2deg); } 50% { transform: rotate(2deg); } }
    .flag-icon { position: absolute; top: -8px; left: -8px; font-size: 1.2rem; z-index: 10; animation: flagBounce 1s ease-in-out infinite; }
    @keyframes flagBounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-3px); } }
    .attack-stack-area { flex: 1; }
    .stack-label { color: #888; font-size: 0.7rem; margin-bottom: 4px; }
    .attack-stack { display: flex; flex-wrap: wrap; gap: 4px; min-height: 50px; }
    .power-display { margin-top: 6px; padding: 6px 10px; background: rgba(255, 215, 0, 0.15); border-radius: 6px; display: flex; justify-content: space-between; align-items: center; }
    .power-value { font-size: 1.1rem; font-weight: bold; color: #ffd700; }
    .power-diff { font-size: 0.75rem; padding: 2px 6px; border-radius: 4px; }
    .power-diff.winning { background: #2ecc71; color: #fff; }
    .power-diff.losing { background: #e74c3c; color: #fff; }
    .power-diff.tied { background: #f39c12; color: #fff; }
    .bench-area { margin-top: 8px; padding-top: 8px; border-top: 1px solid rgba(255,255,255,0.1); }
    .bench-label { color: #888; font-size: 0.65rem; margin-bottom: 4px; }
    .bench-slots { display: flex; flex-wrap: wrap; gap: 4px; }
    .bench-slot { background: rgba(100, 100, 100, 0.3); border: 1px solid #444; border-radius: 4px; padding: 3px 6px; font-size: 0.65rem; color: #aaa; cursor: pointer; transition: all 0.2s; display: flex; align-items: center; gap: 3px; }
    .bench-slot:hover { background: rgba(100, 100, 100, 0.5); border-color: #666; }
    .bench-slot.has-bench-ability { border-color: #2ecc71; background: rgba(46, 204, 113, 0.15); box-shadow: 0 0 10px rgba(46, 204, 113, 0.6), inset 0 0 5px rgba(46, 204, 113, 0.2); animation: benchGlow 2s ease-in-out infinite; }
    @keyframes benchGlow { 0%, 100% { box-shadow: 0 0 10px rgba(46, 204, 113, 0.6), inset 0 0 5px rgba(46, 204, 113, 0.2); } 50% { box-shadow: 0 0 18px rgba(46, 204, 113, 0.9), inset 0 0 8px rgba(46, 204, 113, 0.4); } }
    .bench-slot-count { background: #666; color: #fff; padding: 1px 4px; border-radius: 3px; font-size: 0.6rem; font-weight: bold; }
    .bench-ability-indicator { color: #2ecc71; font-size: 0.6rem; font-weight: bold; text-shadow: 0 0 5px rgba(46, 204, 113, 0.8); }
    .battle-card { width: 45px; height: 60px; border-radius: 6px; display: flex; flex-direction: column; align-items: center; justify-content: center; border: 2px solid rgba(255,255,255,0.3); position: relative; cursor: pointer; transition: transform 0.2s; }
    .battle-card:hover { transform: scale(1.1); z-index: 10; }
    .battle-card.has-ability { border-color: #9b59b6; box-shadow: 0 0 8px rgba(155, 89, 182, 0.5); }
    .battle-card.just-drawn { animation: cardReveal 0.6s ease-out; }
    @keyframes cardReveal { 0% { transform: scale(0) rotateY(180deg); opacity: 0; } 50% { transform: scale(1.3) rotateY(90deg); opacity: 1; } 100% { transform: scale(1) rotateY(0deg); opacity: 1; } }
    .card-name { font-size: 0.5rem; color: #fff; text-align: center; padding: 0 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 100%; }
    .card-power { font-size: 0.85rem; font-weight: bold; color: #ffd700; }
    .card-ability-indicator { position: absolute; top: 1px; right: 1px; font-size: 0.5rem; }
    .vs-divider { display: flex; align-items: center; justify-content: center; padding: 4px 0; position: relative; }
    .vs-text { background: linear-gradient(135deg, #ff6b6b, #ffd700); -webkit-background-clip: text; -webkit-text-fill-color: transparent; font-size: 1rem; font-weight: bold; }
    .battle-log-panel { flex: 1; max-width: 200px; background: rgba(0,0,0,0.4); border-radius: 10px; padding: 8px; display: flex; flex-direction: column; }
    .log-title { color: #888; font-size: 0.75rem; margin-bottom: 6px; padding-bottom: 4px; border-bottom: 1px solid #333; }
    .log-entries { flex: 1; overflow-y: auto; font-size: 0.65rem; }
    .log-entry { padding: 3px 0; border-bottom: 1px solid rgba(255,255,255,0.05); color: #aaa; }
    .log-entry.flag-change { color: #ffd700; font-weight: bold; }
    .log-entry.ability { color: #9b59b6; background: rgba(155, 89, 182, 0.1); padding: 4px 6px; border-radius: 4px; margin: 2px 0; }
    .log-entry.bench-effect { color: #2ecc71; background: rgba(46, 204, 113, 0.1); padding: 4px 6px; border-radius: 4px; margin: 2px 0; }
    .battle-controls { padding: 10px; background: rgba(0,0,0,0.4); display: flex; justify-content: center; gap: 10px; }
    .battle-btn { padding: 12px 28px; border-radius: 8px; font-size: 0.95rem; font-weight: bold; cursor: pointer; transition: all 0.2s; border: none; }
    .battle-btn-primary { background: linear-gradient(135deg, #ff6b6b, #ff8e53); color: #fff; }
    .battle-btn-primary:hover:not(:disabled) { transform: scale(1.05); box-shadow: 0 4px 20px rgba(255, 107, 107, 0.4); }
    .battle-btn:disabled { opacity: 0.4; cursor: not-allowed; }
    .waiting-text { color: #888; font-size: 0.8rem; }
    .flag-transfer-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.85); display: flex; flex-direction: column; align-items: center; justify-content: center; z-index: 1000; animation: fadeIn 0.3s; }
    .flag-transfer-container { position: relative; width: 300px; height: 200px; }
    .flag-transfer-from { position: absolute; top: 0; left: 50%; transform: translateX(-50%); font-size: 3rem; opacity: 0.5; }
    .flag-transfer-to { position: absolute; bottom: 0; left: 50%; transform: translateX(-50%); font-size: 3rem; }
    .flag-moving { position: absolute; font-size: 4rem; left: 50%; transform: translateX(-50%); z-index: 10; }
    .flag-moving.to-me { top: 0; animation: flagMoveDown 1.5s ease-in-out forwards; }
    .flag-moving.to-opponent { bottom: 0; animation: flagMoveUp 1.5s ease-in-out forwards; }
    @keyframes flagMoveDown { 0% { top: 0; transform: translateX(-50%) scale(1); } 50% { top: 50%; transform: translateX(-50%) scale(1.5) rotate(10deg); } 100% { top: calc(100% - 60px); transform: translateX(-50%) scale(1); } }
    @keyframes flagMoveUp { 0% { bottom: 0; transform: translateX(-50%) scale(1); } 50% { bottom: 50%; transform: translateX(-50%) scale(1.5) rotate(-10deg); } 100% { bottom: calc(100% - 60px); transform: translateX(-50%) scale(1); } }
    .flag-capture-text { font-size: 1.5rem; font-weight: bold; margin-top: 30px; text-shadow: 0 0 20px currentColor; animation: textPulse 0.5s ease-out 0.3s both; }
    @keyframes textPulse { 0% { transform: scale(0.8); opacity: 0; } 50% { transform: scale(1.2); } 100% { transform: scale(1); opacity: 1; } }
    .flag-capture-text.win { color: #2ecc71; }
    .flag-capture-text.lose { color: #e74c3c; }
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    .battle-result-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.9); display: flex; flex-direction: column; align-items: center; justify-content: center; z-index: 2000; }
    .result-icon { font-size: 4rem; margin-bottom: 16px; }
    .result-text { font-size: 1.8rem; font-weight: bold; }
    .result-text.win { color: #ffd700; }
    .result-text.lose { color: #888; }
    .result-reason { color: #aaa; margin-top: 8px; font-size: 0.9rem; }
    .result-btn { margin-top: 24px; padding: 12px 32px; background: linear-gradient(135deg, #00fff5, #00d4aa); border: none; border-radius: 8px; color: #1a1a2e; font-size: 1rem; font-weight: bold; cursor: pointer; }
    .ability-popup { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); background: linear-gradient(135deg, #9b59b6, #8e44ad); color: #fff; padding: 12px 24px; border-radius: 8px; font-size: 1rem; font-weight: bold; z-index: 500; animation: abilityPopup 1.5s ease-out forwards; box-shadow: 0 0 30px rgba(155, 89, 182, 0.8); }
    @keyframes abilityPopup { 0% { transform: translate(-50%, -50%) scale(0.8); opacity: 0; } 20% { transform: translate(-50%, -50%) scale(1.1); opacity: 1; } 80% { transform: translate(-50%, -50%) scale(1); opacity: 1; } 100% { transform: translate(-50%, -50%) scale(0.8); opacity: 0; } }
  `;
  document.head.appendChild(style);
}

function getCategoryColor(category: string): string {
  const colors: Record<string, string> = {
    warrior: "#e74c3c", thief: "#9b59b6", mage: "#3498db", machine: "#95a5a6", undead: "#1abc9c",
    dragon: "#e67e22", spirit: "#00bcd4", beast: "#8d6e63", angel: "#ffd700", demon: "#8e44ad", golem: "#607d8b",
    castle: "#8b4513", city: "#4682b4", space: "#483d8b", movie: "#dc143c", ship: "#20b2aa", ghost: "#4b0082", park: "#ff69b4"
  };
  return colors[category] ?? "#666";
}

function getCategoryIcon(category: string): string {
  const icons: Record<string, string> = {
    warrior: "⚔️", thief: "🗡️", mage: "🔮", machine: "⚙️", undead: "💀",
    dragon: "🐉", spirit: "✨", beast: "🐺", angel: "👼", demon: "😈", golem: "🗿",
    castle: "🏰", city: "🏙️", space: "🚀", movie: "🎬", ship: "🚢", ghost: "👻", park: "🎡"
  };
  return icons[category] ?? "?";
}

function hasBenchAbility(def: CardDef): boolean {
  if (!def.abilities || def.abilities.length === 0) return false;
  return def.abilities.some((ability) => ability.timing === "onBench");
}

function getBenchAbilityHint(def: CardDef): string {
  if (!def.abilities || def.abilities.length === 0) return "";
  for (const ability of def.abilities) {
    if (ability.timing === "onBench") {
      if (ability.effect.type === "powerBoost") {
        const target = ability.effect.targetCategory ? getCategoryIcon(ability.effect.targetCategory) : "";
        return `${target}+${ability.effect.value ?? 1}`;
      }
    }
  }
  return "効果";
}

export function renderMatchBattle(
  matchPhase: MatchPhase,
  myPlayerId: string,
  opponentPlayerId: string,
  opponentIsBot: boolean,
  onComplete: (result: { winnerId: string; loserId: string; reason: string }) => void,
  isMultiplayer: boolean = false,
  isHost: boolean = false,
  matchIndex: number = 0
): void {
  injectBattleStyles();
  
  // swapコールバックを設定
  matchPhase.setSwapCallback({
    showSwapUI: (playerId, cards, count, onSwapComplete, mode) => {
      if (playerId === myPlayerId) {
        showSwapModal(cards, count, async (result) => {
          onSwapComplete(result);
          await delay(100);
          if (battleState.matchPhase) {
            if (battleState.isMultiplayer && !battleState.opponentIsBot) {
              await syncMatchStateToServer();
            }
            renderBattleUI(onComplete);
          }
        }, mode);
      } else if (opponentIsBot) {
        if (cards.length > count) {
          const shuffled = [...cards].sort(() => Math.random() - 0.5);
          onSwapComplete(shuffled.slice(0, count));
        } else {
          const shuffled = [...cards].sort(() => Math.random() - 0.5);
          onSwapComplete(shuffled);
        }
      } else {
        console.log('[Battle] Opponent swap effect - waiting for sync');
      }
    },
  });

  // move効果コールバック設定
  matchPhase.setMoveCallback({
    showMoveUI: (playerId, cards, maxSelect, title, description, onMoveComplete) => {
      if (playerId === myPlayerId) {
        showMoveCardModal(cards, maxSelect, title, description, async (selected) => {
          onMoveComplete(selected);
          await delay(100);
          if (battleState.matchPhase) {
            if (battleState.isMultiplayer && !battleState.opponentIsBot) {
              await syncMatchStateToServer();
            }
            renderBattleUI(onComplete);
          }
        });
      } else if (opponentIsBot) {
        const sorted = [...cards].sort((a, b) => a.currentPower - b.currentPower);
        onMoveComplete(sorted.slice(0, maxSelect));
      } else {
        console.log('[Battle] Opponent move effect - waiting for sync');
      }
    },
  });

  battleState = {
    matchPhase,
    myPlayerId,
    opponentPlayerId,
    isMyTurn: false,
    opponentIsBot,
    botTimer: null,
    lastDrawnCard: null,
    showFlagCapture: false,
    flagCapturer: null,
    isAnimating: false,
    lastAbilityMessage: null,
    isMultiplayer,
    isHost,
    matchIndex,
    lastSyncTime: Date.now(),
    pollTimer: null,
    waitingForOpponent: false,
    pendingFlagCapture: false,
    onSyncComplete: null,
  };
  
  matchPhase.start();
  
  if (isMultiplayer && isHost) {
    syncMatchStateToServer();
  }
  
  renderBattleUI(onComplete);
  
  if (isMultiplayer && !opponentIsBot) {
    startMultiplayerPolling(onComplete);
  }
}

function renderBattleUI(onComplete: (result: { winnerId: string; loserId: string; reason: string }) => void): void {
  if (!battleState.matchPhase) return;
  const state = battleState.matchPhase.getState();
  const myIdx = state.players.findIndex((p) => p.playerId === battleState.myPlayerId);
  const opponentIdx = myIdx === 0 ? 1 : 0;
  const me = state.players[myIdx];
  battleState.isMyTurn = me.isAttacker;
  const logs = state.log.slice(-10);

  render(`
    <div class="match-battle-container">
      <div class="battle-header">
        <div class="battle-title">⚔️ BATTLE</div>
        <div class="turn-indicator">Turn ${state.turnCount} - ${battleState.isMyTurn ? "🎯 あなたのターン" : "⏳ 相手のターン"}</div>
      </div>
      <div class="battle-main">
        <div class="battle-field">
          ${renderPlayerZone(opponentIdx, false, state)}
          <div class="vs-divider"><span class="vs-text">⚔️ VS ⚔️</span></div>
          ${renderPlayerZone(myIdx, true, state)}
        </div>
        <div class="battle-log-panel">
          <div class="log-title">📜 バトルログ</div>
          <div class="log-entries" id="logEntries">
            ${logs
              .map((log) => {
                const isFlag = log.action === "flagTaken";
                const isAbility = log.action === "abilityTrigger";
                const isBenchEffect = log.message.includes("シナジー") || log.message.includes("パワー修正");
                return `<div class="log-entry ${isFlag ? "flag-change" : ""} ${isAbility ? "ability" : ""} ${isBenchEffect ? "bench-effect" : ""}">T${log.turn}: ${log.message}</div>`;
              })
              .join("")}
          </div>
        </div>
      </div>
      <div class="battle-controls">
        ${
          battleState.isMyTurn && !battleState.isAnimating
            ? `<button class="battle-btn battle-btn-primary" id="btnDrawCard">🃏 カードをめくる</button>`
            : battleState.isAnimating
            ? `<span class="waiting-text">⏳ アニメーション中...</span>`
            : `<span class="waiting-text">相手のターンを待っています...</span>`
        }
      </div>
    </div>
    ${battleState.showFlagCapture ? renderFlagTransferOverlay() : ""}
  `);

  const logEl = document.getElementById("logEntries");
  if (logEl) logEl.scrollTop = logEl.scrollHeight;

  document.getElementById("btnDrawCard")?.addEventListener("click", () => {
    if (!battleState.isAnimating) handleDrawCard(onComplete);
  });
  document.querySelectorAll(".battle-card").forEach((el) => {
    el.addEventListener("click", () => {
      const instanceId = el.getAttribute("data-instance-id");
      if (instanceId) {
        const card = findCardByInstanceId(instanceId);
        if (card) showCardDetail(card);
      }
    });
  });
  document.querySelectorAll(".bench-slot").forEach((el) => {
    el.addEventListener("click", () => {
      const defId = el.getAttribute("data-def-id");
      if (defId) {
        const def = cardDefs.get(defId);
        if (def) showBenchCardDetail(def);
      }
    });
  });

  if (!battleState.isMyTurn && !battleState.isAnimating && battleState.opponentIsBot && state.status === "inProgress") {
    if (!battleState.matchPhase?.isWaitingForInput()) {
      scheduleBotTurn(onComplete);
    }
  }
}

function renderPlayerZone(playerIdx: number, isMe: boolean, state: MatchState): string {
  if (!battleState.matchPhase) return "";
  const player = state.players[playerIdx];
  const field = player.field;
  const opponentIdx = playerIdx === 0 ? 1 : 0;
  const stack = battleState.matchPhase.getAttackStack(playerIdx);
  const stackPower = stack.reduce((sum, c) => sum + c.currentPower, 0);
  const stackBasePower = stack.reduce((sum, c) => {
    const def = cardDefs.get(c.defId);
    return sum + (def?.basePower ?? c.currentPower);
  }, 0);
  const stackBonus = stackPower - stackBasePower;
  
  const opponentStack = battleState.matchPhase.getAttackStack(opponentIdx);
  const opponentStackPower = opponentStack.reduce((sum, c) => sum + c.currentPower, 0);
  const isAttacker = player.isAttacker;
  const isDefender = !isAttacker;
  const hasFlag = isDefender && field.flagHolder !== null;
  const flagPower = hasFlag && field.flagHolder ? field.flagHolder.currentPower : 0;
  let flagBasePower = 0;
  let flagBonus = 0;
  if (hasFlag && field.flagHolder) {
    const flagDef = cardDefs.get(field.flagHolder.defId);
    flagBasePower = flagDef?.basePower ?? field.flagHolder.currentPower;
    flagBonus = flagPower - flagBasePower;
  }
  
  const zoneClass = `player-zone ${isMe ? "my-zone" : "opponent-zone"} ${hasFlag ? "has-flag" : ""} ${isAttacker ? "is-attacker" : ""}`;
  const icon = isMe ? "👤" : "🤖";
  const label = isMe ? "YOU" : "Opponent";

  let powerDiff = 0, powerDiffClass = "tied", powerDiffText = "同点";
  if (isAttacker) {
    powerDiff = stackPower - flagPower;
    if (powerDiff > 0) { powerDiffClass = "winning"; powerDiffText = `+${powerDiff} 勝利中`; }
    else if (powerDiff < 0) { powerDiffClass = "losing"; powerDiffText = `${powerDiff} あと${Math.abs(powerDiff) + 1}必要`; }
  } else if (hasFlag) {
    powerDiff = flagPower - opponentStackPower;
    if (powerDiff > 0) { powerDiffClass = "winning"; powerDiffText = `+${powerDiff} 守り中`; }
    else if (powerDiff < 0) { powerDiffClass = "losing"; powerDiffText = `危険! ${Math.abs(powerDiff)}超過`; }
  }

  return `
    <div class="${zoneClass}" id="${isMe ? "myZone" : "opponentZone"}">
      <div class="zone-header">
        <span class="player-name ${isMe ? "me" : "opponent"}">${icon} ${label}</span>
        ${isAttacker ? '<span class="attacker-badge">🗡️ ATTACK</span>' : '<span class="defender-badge">🛡️ DEFEND</span>'}
      </div>
      <div class="zone-content">
        ${hasFlag ? `<div class="flag-area"><div class="flag-label">🚩 FLAG</div><div class="flag-card" id="${isMe ? "myFlag" : "opponentFlag"}"><div class="flag-icon">🚩</div>${renderBattleCard(field.flagHolder!, false)}</div></div>` : ""}
        <div class="attack-stack-area">
          <div class="stack-label">📤 アタックスタック (${stack.length}枚)</div>
          <div class="attack-stack">${stack.map((card, idx) => {
            const isLast = idx === stack.length - 1;
            const isJustDrawn = isLast && battleState.lastDrawnCard?.instanceId === card.instanceId;
            return renderBattleCard(card, isJustDrawn);
          }).join("")}</div>
          ${isAttacker || hasFlag ? `<div class="power-display"><span class="power-value">⚡ ${isAttacker ? stackPower : flagPower}${(isAttacker ? stackBonus : flagBonus) !== 0 ? `<span style="font-size: 0.7em; opacity: 0.8; margin-left: 4px;">(${isAttacker ? stackBasePower : flagBasePower}${(isAttacker ? stackBonus : flagBonus) > 0 ? '+' : ''}${isAttacker ? stackBonus : flagBonus})</span>` : ''}</span><span class="power-diff ${powerDiffClass}">${powerDiffText}</span></div>` : ""}
        </div>
      </div>
      <div class="bench-area">
        <div class="bench-label">ベンチ (${battleState.matchPhase!.getBenchSlots(playerIdx).length}/6)${battleState.matchPhase!.getExileCount(playerIdx) > 0 ? `<span style="margin-left: 12px; color: #e74c3c;">除外: ${battleState.matchPhase!.getExileCount(playerIdx)}枚</span>` : ''}</div>
        <div class="bench-slots">${renderBenchSlots(playerIdx)}</div>
      </div>
    </div>
  `;
}

function renderBattleCard(card: CardInstance, isJustDrawn: boolean): string {
  const def = cardDefs.get(card.defId);
  if (!def) return "";
  const bgColor = getCategoryColor(def.category);
  const hasAbility = def.abilities && def.abilities.length > 0;
  const basePower = def.basePower;
  const currentPower = card.currentPower;
  const powerDiff = currentPower - basePower;
  let powerDisplay = `${currentPower}`;
  if (powerDiff !== 0) {
    powerDisplay = `${currentPower}<span style="font-size: 0.5rem; opacity: 0.7;">(${basePower}${powerDiff > 0 ? '+' : ''}${powerDiff})</span>`;
  }
  return `
    <div class="battle-card ${hasAbility ? "has-ability" : ""} ${isJustDrawn ? "just-drawn" : ""}" data-instance-id="${card.instanceId}" style="background: linear-gradient(135deg, ${bgColor}88 0%, ${bgColor}44 100%);">
      ${hasAbility ? '<span class="card-ability-indicator">✨</span>' : ""}
      <span class="card-power">${powerDisplay}</span>
      <span class="card-name">${def.name}</span>
    </div>
  `;
}

function renderBenchSlots(playerIdx: number): string {
  if (!battleState.matchPhase) return "";
  const slots = battleState.matchPhase.getBenchSlots(playerIdx);
  return slots.map((slot) => {
    const def = cardDefs.get(slot.defId);
    if (!def) return "";
    const catIcon = getCategoryIcon(def.category);
    const bgColor = getCategoryColor(def.category);
    const hasBench = hasBenchAbility(def);
    const benchHint = hasBench ? getBenchAbilityHint(def) : "";
    return `<div class="bench-slot ${hasBench ? "has-bench-ability" : ""}" data-def-id="${slot.defId}" style="border-left: 3px solid ${bgColor};"><span>${catIcon}</span><span>${def.name}</span><span class="bench-slot-count">x${slot.count}</span>${hasBench ? `<span class="bench-ability-indicator">${benchHint}</span>` : ""}</div>`;
  }).join("");
}

async function handleDrawCard(onComplete: (result: { winnerId: string; loserId: string; reason: string }) => void): Promise<void> {
  if (!battleState.matchPhase || !battleState.isMyTurn || battleState.isAnimating) return;
  battleState.isAnimating = true;

  const beforeState = battleState.matchPhase.getState();
  const beforeLogLength = beforeState.log.length;
  const myIdx = beforeState.players.findIndex((p) => p.playerId === battleState.myPlayerId);

  const newState = battleState.matchPhase.processTurn();

  const stack = battleState.matchPhase.getAttackStack(myIdx);
  if (stack.length > 0) battleState.lastDrawnCard = stack[stack.length - 1];

  const newLogs = newState.log.slice(beforeLogLength);
  const abilityLog = newLogs.find((l) => l.action === "abilityTrigger");
  const flagTakenLog = newLogs.find((l) => l.action === "flagTaken");

  if (abilityLog) battleState.lastAbilityMessage = abilityLog.message;

  renderBattleUI(onComplete);
  if (battleState.lastAbilityMessage) {
    showAbilityPopup(battleState.lastAbilityMessage);
    battleState.lastAbilityMessage = null;
  }

  await delay(CARD_REVEAL_DELAY);

  if (battleState.matchPhase?.isWaitingForInput()) {
    console.log('[Battle] Waiting for input (swap/move)...');
    if (battleState.isMultiplayer && !battleState.opponentIsBot) {
      await syncMatchStateToServer();
    }
    await waitForInputComplete();
    console.log('[Battle] Input complete, continuing...');
  }

  if (battleState.isMultiplayer && !battleState.opponentIsBot) {
    await syncMatchStateToServer();
  }

  if (flagTakenLog) {
    battleState.showFlagCapture = true;
    battleState.flagCapturer = "me";
    renderBattleUI(onComplete);
    await delay(FLAG_TRANSFER_DELAY);
    battleState.showFlagCapture = false;
    battleState.flagCapturer = null;
    battleState.lastDrawnCard = null;
    if (battleState.isMultiplayer && !battleState.opponentIsBot) {
      await syncMatchStateToServer();
    }
    battleState.isAnimating = false;
    const state = battleState.matchPhase?.getState();
    if (state?.status === "finished") {
      showResult(state, onComplete);
    } else {
      renderBattleUI(onComplete);
    }
    return;
  }

  if (newState.status === "finished") {
    battleState.isAnimating = false;
    showResult(newState, onComplete);
    return;
  }

  battleState.isAnimating = false;
  battleState.lastDrawnCard = null;
  renderBattleUI(onComplete);
}

function waitForInputComplete(): Promise<void> {
  return new Promise((resolve) => {
    const checkInterval = setInterval(() => {
      if (!battleState.matchPhase?.isWaitingForInput()) {
        clearInterval(checkInterval);
        resolve();
      }
    }, 100);
  });
}

function scheduleBotTurn(onComplete: (result: { winnerId: string; loserId: string; reason: string }) => void): void {
  if (battleState.botTimer) clearTimeout(battleState.botTimer);
  battleState.botTimer = window.setTimeout(async () => {
    if (!battleState.matchPhase || battleState.isAnimating) return;
    if (battleState.matchPhase.isWaitingForInput()) {
      scheduleBotTurn(onComplete);
      return;
    }
    battleState.isAnimating = true;
    const state = battleState.matchPhase.getState();
    if (state.status !== "inProgress") {
      battleState.isAnimating = false;
      return;
    }
    const opponentIdx = state.players.findIndex((p) => p.playerId === battleState.opponentPlayerId);
    const beforeLogLength = state.log.length;
    const newState = battleState.matchPhase.processTurn();
    const stack = battleState.matchPhase.getAttackStack(opponentIdx);
    if (stack.length > 0) battleState.lastDrawnCard = stack[stack.length - 1];
    const newLogs = newState.log.slice(beforeLogLength);
    const abilityLog = newLogs.find((l) => l.action === "abilityTrigger");
    const flagTakenLog = newLogs.find((l) => l.action === "flagTaken");
    if (abilityLog) battleState.lastAbilityMessage = abilityLog.message;
    renderBattleUI(onComplete);
    if (battleState.lastAbilityMessage) {
      showAbilityPopup(battleState.lastAbilityMessage);
      battleState.lastAbilityMessage = null;
    }
    await delay(CARD_REVEAL_DELAY);
    if (flagTakenLog) {
      battleState.showFlagCapture = true;
      battleState.flagCapturer = "opponent";
      renderBattleUI(onComplete);
      await delay(FLAG_TRANSFER_DELAY);
      battleState.showFlagCapture = false;
      battleState.flagCapturer = null;
      battleState.lastDrawnCard = null;
      battleState.isAnimating = false;
      const currentState = battleState.matchPhase?.getState();
      if (currentState?.status === "finished") {
        showResult(currentState, onComplete);
      } else {
        renderBattleUI(onComplete);
      }
      return;
    }
    if (newState.status === "finished") {
      battleState.isAnimating = false;
      showResult(newState, onComplete);
      return;
    }
    battleState.isAnimating = false;
    battleState.lastDrawnCard = null;
    renderBattleUI(onComplete);
  }, BOT_TURN_DELAY);
}

function showAbilityPopup(message: string): void {
  const popup = document.createElement("div");
  popup.className = "ability-popup";
  popup.textContent = `✨ ${message}`;
  document.body.appendChild(popup);
  setTimeout(() => popup.remove(), 1500);
}

function renderFlagTransferOverlay(): string {
  const isWin = battleState.flagCapturer === "me";
  return `
    <div class="flag-transfer-overlay">
      <div class="flag-transfer-container">
        <div class="flag-transfer-from">🤖</div>
        <div class="flag-moving ${isWin ? "to-me" : "to-opponent"}">🚩</div>
        <div class="flag-transfer-to">👤</div>
      </div>
      <div class="flag-capture-text ${isWin ? "win" : "lose"}">${isWin ? "🎉 フラッグ奪取!" : "😱 フラッグを奪われた!"}</div>
    </div>
  `;
}

function showResult(state: MatchState, onComplete: (result: { winnerId: string; loserId: string; reason: string }) => void): void {
  if (!battleState.matchPhase) return;
  const matchResult = battleState.matchPhase.getResult();
  if (!matchResult) return;
  const winnerId = matchResult.winnerId;
  const loserId = matchResult.loserId;
  const isWin = winnerId === battleState.myPlayerId;
  const lastLog = state.log[state.log.length - 1];
  let reason = "flag";
  if (lastLog?.message?.includes("デッキ切れ")) reason = "deckout";
  else if (lastLog?.message?.includes("ベンチ")) reason = "benchfull";
  const reasonText: Record<string, string> = { flag: "フラッグ奪取", deckout: "デッキ切れ", benchfull: "ベンチ溢れ" };
  const app = document.getElementById("app");
  if (app) {
    app.innerHTML = `<div class="battle-result-overlay"><div class="result-icon">${isWin ? "🏆" : "💀"}</div><div class="result-text ${isWin ? "win" : "lose"}">${isWin ? "VICTORY!" : "DEFEAT..."}</div><div class="result-reason">${reasonText[reason] || reason}</div><button class="result-btn" id="btnContinue">続ける</button></div>`;
    document.getElementById("btnContinue")?.addEventListener("click", () => onComplete({ winnerId, loserId, reason }));
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function findCardByInstanceId(instanceId: string): CardInstance | null {
  if (!battleState.matchPhase) return null;
  const state = battleState.matchPhase.getState();
  for (const player of state.players) {
    for (const card of player.field.bench) {
      if (card.instanceId === instanceId) return card;
    }
    if (player.field.flagHolder?.instanceId === instanceId) return player.field.flagHolder;
  }
  for (let i = 0; i < 2; i++) {
    const stack = battleState.matchPhase.getAttackStack(i);
    for (const card of stack) {
      if (card.instanceId === instanceId) return card;
    }
  }
  return null;
}

function showBenchCardDetail(def: CardDef): void {
  const modal = document.createElement("div");
  modal.style.cssText = `position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); display: flex; align-items: center; justify-content: center; z-index: 3000;`;
  const hasAbility = def.abilities && def.abilities.length > 0;
  const abilityText = hasAbility ? def.abilities[0].effect.description : "なし";
  const hasBench = hasBenchAbility(def);
  modal.innerHTML = `<div style="background: linear-gradient(180deg, ${getCategoryColor(def.category)}44 0%, #1a1a2e 100%); border: 2px solid ${getCategoryColor(def.category)}; border-radius: 12px; padding: 20px; max-width: 280px; text-align: center;"><div style="font-size: 2rem; margin-bottom: 8px;">${getCategoryIcon(def.category)}</div><div style="color: #fff; font-size: 1.2rem; font-weight: bold; margin-bottom: 4px;">${def.name}</div><div style="color: #ffd700; font-size: 1.5rem; font-weight: bold; margin-bottom: 12px;">⚡ ${def.basePower}</div><div style="color: #aaa; font-size: 0.8rem; margin-bottom: 8px;">Tier ${def.tier} | ${def.category}</div>${hasAbility ? `<div style="background: rgba(${hasBench ? "46, 204, 113" : "155, 89, 182"}, 0.2); border: 1px solid ${hasBench ? "#2ecc71" : "#9b59b6"}; border-radius: 6px; padding: 8px; margin-top: 8px;"><div style="color: ${hasBench ? "#2ecc71" : "#9b59b6"}; font-size: 0.75rem; margin-bottom: 4px;">${hasBench ? "🌟 ベンチ効果" : "✨ 能力"}</div><div style="color: #fff; font-size: 0.8rem;">${abilityText}</div></div>` : ""}<button style="margin-top: 16px; padding: 8px 24px; background: #444; border: none; border-radius: 6px; color: #fff; cursor: pointer;">閉じる</button></div>`;
  modal.addEventListener("click", (e) => {
    if (e.target === modal || (e.target as HTMLElement).tagName === "BUTTON") modal.remove();
  });
  document.body.appendChild(modal);
}

export function cleanupMatchBattle(): void {
  if (battleState.botTimer) { clearTimeout(battleState.botTimer); battleState.botTimer = null; }
  if (battleState.pollTimer) { clearTimeout(battleState.pollTimer); battleState.pollTimer = null; }
  battleState.matchPhase = null;
  battleState.lastDrawnCard = null;
  battleState.showFlagCapture = false;
  battleState.isAnimating = false;
  battleState.lastAbilityMessage = null;
  battleState.isMultiplayer = false;
  battleState.waitingForOpponent = false;
}

// ========== マルチプレイ同期関数 ==========

async function syncMatchStateToServer(): Promise<void> {
  if (!battleState.matchPhase || !battleState.isMultiplayer) return;
  const multiplayer = getMultiplayerManager();
  const state = battleState.matchPhase.getState();
  const player1Field = extractFieldForSync(0);
  const player2Field = extractFieldForSync(1);
  const syncState: MatchSyncState = {
    matchId: state.matchId,
    player1Id: state.players[0].playerId,
    player2Id: state.players[1].playerId,
    player1Field,
    player2Field,
    player1Deck: player1Field.deck,
    player2Deck: player2Field.deck,
    firstPlayerId: state.players.find(p => !p.isAttacker)?.playerId || state.players[0].playerId,
    turnCount: state.turnCount,
    currentAttackerId: state.players.find(p => p.isAttacker)?.playerId || '',
    status: state.status,
    lastAction: { type: 'sync', playerId: multiplayer.getCurrentUserId() || '', timestamp: Date.now() },
    lastActionTime: Date.now(),
    winner: state.winner,
    isWaitingForInput: battleState.matchPhase.isWaitingForInput(),
    round: state.round,
    matchIndex: battleState.matchIndex,  // マッチインデックスを追加
  };
  await multiplayer.updateMatchState(syncState);
  battleState.lastSyncTime = Date.now();
}

function extractFieldForSync(playerIdx: number): PlayerFieldSync {
  if (!battleState.matchPhase) return { deck: [], attackStack: [], benchSlots: [], flagHolder: null, exile: [], isAttacker: false };
  const state = battleState.matchPhase.getState();
  const player = state.players[playerIdx];
  const field = player.field as any;
  const deck: CardInstanceSync[] = (field._deck || []).map((card: CardInstance) => ({ defId: card.defId, instanceId: card.instanceId, currentPower: card.currentPower, isRevealed: card.isRevealed }));
  const attackStack: CardInstanceSync[] = (field._attackStack || []).map((card: CardInstance) => ({ defId: card.defId, instanceId: card.instanceId, currentPower: card.currentPower, isRevealed: card.isRevealed }));
  const benchSlots: BenchSlotSync[] = (field._benchSlots || []).map((slot: any) => ({ defId: slot.defId, cards: slot.cards.map((c: CardInstance) => ({ defId: c.defId, instanceId: c.instanceId, currentPower: c.currentPower, isRevealed: c.isRevealed })) }));
  const flagHolder: CardInstanceSync | null = field.flagHolder ? { defId: field.flagHolder.defId, instanceId: field.flagHolder.instanceId, currentPower: field.flagHolder.currentPower, isRevealed: field.flagHolder.isRevealed } : null;
  const exile: CardInstanceSync[] = (field.exile || []).map((card: CardInstance) => ({ defId: card.defId, instanceId: card.instanceId, currentPower: card.currentPower, isRevealed: card.isRevealed }));
  return { deck, attackStack, benchSlots, flagHolder, exile, isAttacker: player.isAttacker };
}

function startMultiplayerPolling(onComplete: (result: { winnerId: string; loserId: string; reason: string }) => void): void {
  if (battleState.pollTimer) clearTimeout(battleState.pollTimer);
  pollForOpponentTurn(onComplete);
}

async function pollForOpponentTurn(onComplete: (result: { winnerId: string; loserId: string; reason: string }) => void): Promise<void> {
  if (!battleState.matchPhase || !battleState.isMultiplayer) return;
  if (battleState.isMyTurn || battleState.isAnimating || battleState.opponentIsBot) {
    battleState.pollTimer = window.setTimeout(() => pollForOpponentTurn(onComplete), MULTIPLAYER_POLL_INTERVAL);
    return;
  }
  const multiplayer = getMultiplayerManager();
  try {
    const serverMatch = await multiplayer.pollMatchUpdate(battleState.lastSyncTime, 3000, battleState.matchIndex);
    if (serverMatch && serverMatch.lastActionTime > battleState.lastSyncTime) {
      console.log('[Battle] Received opponent turn update, waitingForInput:', serverMatch.isWaitingForInput, 'matchIndex:', battleState.matchIndex);
      battleState.lastSyncTime = serverMatch.lastActionTime;
      if (serverMatch.isWaitingForInput === true) {
        console.log('[Battle] Opponent is waiting for input, continue polling...');
        battleState.pollTimer = window.setTimeout(() => pollForOpponentTurn(onComplete), MULTIPLAYER_POLL_INTERVAL);
        return;
      }
      await applyServerMatchState(serverMatch, onComplete);
    }
  } catch (err) {
    console.error('[Battle] Poll error:', err);
  }
  if (battleState.matchPhase && battleState.isMultiplayer && !battleState.opponentIsBot) {
    battleState.pollTimer = window.setTimeout(() => pollForOpponentTurn(onComplete), MULTIPLAYER_POLL_INTERVAL);
  }
}

async function applyServerMatchState(serverMatch: MatchSyncState, onComplete: (result: { winnerId: string; loserId: string; reason: string }) => void): Promise<void> {
  if (!battleState.matchPhase) return;
  battleState.isAnimating = true;
  const currentState = battleState.matchPhase.getState();
  const currentTurn = currentState.turnCount;
  if (serverMatch.turnCount > currentTurn) {
    console.log('[Battle] Applying server state, turn:', currentTurn, '->', serverMatch.turnCount);
    const opponentIdx = currentState.players.findIndex(p => p.playerId === battleState.opponentPlayerId);
    const myIdx = currentState.players.findIndex(p => p.playerId === battleState.myPlayerId);
    const wasMyTurn = currentState.players[myIdx].isAttacker;
    applyFieldStateFromServer(serverMatch);
    const opponentField = opponentIdx === 0 ? serverMatch.player1Field : serverMatch.player2Field;
    if (opponentField.attackStack.length > 0) {
      const lastCard = opponentField.attackStack[opponentField.attackStack.length - 1];
      battleState.lastDrawnCard = { defId: lastCard.defId, instanceId: lastCard.instanceId, currentPower: lastCard.currentPower, isRevealed: lastCard.isRevealed };
    }
    const newState = battleState.matchPhase.getState();
    renderBattleUI(onComplete);
    await delay(CARD_REVEAL_DELAY);
    const isMyTurnNow = newState.players[myIdx].isAttacker;
    if (wasMyTurn !== isMyTurnNow) {
      battleState.showFlagCapture = true;
      battleState.flagCapturer = 'opponent';
      renderBattleUI(onComplete);
      await delay(FLAG_TRANSFER_DELAY);
      battleState.showFlagCapture = false;
      battleState.flagCapturer = null;
    }
    battleState.lastDrawnCard = null;
    if (serverMatch.status === 'finished' || newState.status === 'finished') {
      battleState.isAnimating = false;
      showResult(newState, onComplete);
      return;
    }
  }
  battleState.isAnimating = false;
  renderBattleUI(onComplete);
}

function applyFieldStateFromServer(serverMatch: MatchSyncState): void {
  if (!battleState.matchPhase) return;
  const state = battleState.matchPhase.getState();
  for (let playerIdx = 0; playerIdx < 2; playerIdx++) {
    const serverField = playerIdx === 0 ? serverMatch.player1Field : serverMatch.player2Field;
    const player = state.players[playerIdx];
    const field = player.field as any;
    field._deck = serverField.deck.map(c => ({ defId: c.defId, instanceId: c.instanceId, currentPower: c.currentPower, isRevealed: c.isRevealed }));
    field.deckRemaining = field._deck.length;
    field._attackStack = serverField.attackStack.map(c => ({ defId: c.defId, instanceId: c.instanceId, currentPower: c.currentPower, isRevealed: c.isRevealed }));
    field._benchSlots = serverField.benchSlots.map(slot => ({ defId: slot.defId, cards: slot.cards.map(c => ({ defId: c.defId, instanceId: c.instanceId, currentPower: c.currentPower, isRevealed: c.isRevealed })) }));
    field.bench = field._benchSlots.flatMap((s: any) => s.cards);
    field.flagHolder = serverField.flagHolder ? { defId: serverField.flagHolder.defId, instanceId: serverField.flagHolder.instanceId, currentPower: serverField.flagHolder.currentPower, isRevealed: serverField.flagHolder.isRevealed } : null;
    field.exile = serverField.exile.map(c => ({ defId: c.defId, instanceId: c.instanceId, currentPower: c.currentPower, isRevealed: c.isRevealed }));
    player.isAttacker = serverField.isAttacker;
  }
  (state as any).turnCount = serverMatch.turnCount;
  (state as any).status = serverMatch.status;
  if (serverMatch.winner) (state as any).winner = serverMatch.winner;
}

export async function initMultiplayerMatch(
  player1Id: string,
  player1Deck: CardInstance[],
  player2Id: string,
  player2Deck: CardInstance[],
  firstPlayerId: string,
  round: number,
  isHost: boolean,
  matchIndex: number = 0,  // マッチのインデックス (複数マッチ対応)
  myId?: string,
  opponentId?: string
): Promise<{ matchPhase: MatchPhase; syncState: MatchSyncState } | null> {
  const multiplayer = getMultiplayerManager();
  
  // マッチ準備完了を通知 (両者が通知することで同期を確認)
  await multiplayer.notifyMatchReady(matchIndex);
  
  if (isHost) {
    // マッチ作成側: 相手の準備完了を待ってからマッチを作成
    if (opponentId) {
      console.log('[Game] Waiting for opponent ready before creating match: matchIndex=', matchIndex, 'opponentId=', opponentId);
      const opponentReady = await multiplayer.waitForOpponentMatchReady(matchIndex, opponentId, 15000);
      if (!opponentReady) {
        console.warn('[Game] Opponent not ready, but proceeding with match creation: matchIndex=', matchIndex);
      }
    }
    
    console.log('[Game] Creating match as host: matchIndex=', matchIndex, 'round=', round, 'player1Id=', player1Id, 'player2Id=', player2Id);
    const matchPhase = new MatchPhase(round, player1Id, player1Deck, player2Id, player2Deck, firstPlayerId, false);
    const state = matchPhase.getState();
    const player1Field = extractInitialFieldForSync(matchPhase, 0);
    const player2Field = extractInitialFieldForSync(matchPhase, 1);
    const syncState: MatchSyncState = {
      matchId: state.matchId, player1Id, player2Id, player1Field, player2Field,
      player1Deck: player1Field.deck, player2Deck: player2Field.deck,
      firstPlayerId, turnCount: 0, currentAttackerId: firstPlayerId === player1Id ? player2Id : player1Id,
      status: 'waiting', lastAction: null, lastActionTime: Date.now(),
      round,  // ラウンド番号を追加
      matchIndex,  // マッチインデックスを追加
    };
    
    // リトライロジック: マッチ作成側もリトライ
    const maxRetries = 3;
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const createdMatch = await multiplayer.createMatchState(syncState, matchIndex);
        if (!createdMatch) {
          throw new Error('createMatchState returned null');
        }
        console.log('[Game] Match created successfully: matchIndex=', matchIndex, 'attempt=', attempt);
        return { matchPhase, syncState };
      } catch (err) {
        console.error('[Game] Failed to create match (attempt', attempt, '):', err);
        if (attempt < maxRetries) {
          console.log('[Game] Retrying match creation...');
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }
    
    console.error('[Game] Failed to create match after', maxRetries, 'attempts');
    return null;
  } else {
    // リトライロジック: 最大3回、各10秒待機
    const maxRetries = 3;
    const waitTimePerRetry = 10000;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      console.log('[Game] Waiting for match state from server: matchIndex=', matchIndex, 'round=', round, 'attempt=', attempt);
      
      const syncState = await multiplayer.waitForMatchState(waitTimePerRetry, 0, round, matchIndex);
      
      if (syncState) {
        console.log('[Game] Match state received: matchIndex=', matchIndex, 'player1Id=', syncState.player1Id, 'player2Id=', syncState.player2Id);
        const deck1: CardInstance[] = syncState.player1Deck.map(c => ({ defId: c.defId, instanceId: c.instanceId, currentPower: c.currentPower, isRevealed: c.isRevealed }));
        const deck2: CardInstance[] = syncState.player2Deck.map(c => ({ defId: c.defId, instanceId: c.instanceId, currentPower: c.currentPower, isRevealed: c.isRevealed }));
        const matchPhase = new MatchPhase(round, syncState.player1Id, deck1, syncState.player2Id, deck2, syncState.firstPlayerId, true);
        return { matchPhase, syncState };
      }
      
      if (attempt < maxRetries) {
        console.log('[Game] Retry waiting for match state: attempt=', attempt, '/', maxRetries);
      }
    }
    
    console.error('[Game] Failed to get match state from server after', maxRetries, 'attempts: matchIndex=', matchIndex, 'round=', round);
    return null;
  }
}

function extractInitialFieldForSync(matchPhase: MatchPhase, playerIdx: number): PlayerFieldSync {
  const state = matchPhase.getState();
  const player = state.players[playerIdx];
  const field = player.field as any;
  const deck: CardInstanceSync[] = (field._deck || []).map((card: CardInstance) => ({ defId: card.defId, instanceId: card.instanceId, currentPower: card.currentPower, isRevealed: card.isRevealed }));
  return { deck, attackStack: [], benchSlots: [], flagHolder: null, exile: [], isAttacker: player.isAttacker };
}
